// Menopause Score detail screen. Shows: 14-day trend, semicircle gauge,
// insight callout, score factors (per-symptom severity + tap-to-expand
// 14-day inline chart), hot flashes (event bar + heat row), methodology.
//
// Per Turn 3 trim approvals:
//   - Trim A: severity-tracked sub-charts collapsed to a single 14d line.
//   - Trim B: Mood + HRT adherence sections dropped from this screen.

import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  LineChart, Line, LabelList, ResponsiveContainer, XAxis, YAxis, Tooltip,
  ReferenceLine, BarChart, Bar, Cell,
} from "recharts";
import {
  impactBandFor, generateInsight, generateDailyInsight, computeSmoothedHeadline,
  MIN_LOGGED_FOR_INCLUSION,
  METHODOLOGY_DISCLOSURE,
  rankDriversByImpact, heatCellColor,
  fetchMenoScore,
  type TrackedSymptom,
} from "./menoScore";
import {
  parseLocalDateKey, localDateKey, lastNDates,
} from "./dailyLogs";
import { getScoreLabel } from "./scoreLabel";
import { ScoreV2ChangeNote } from "./MenopauseScoreCard";

const T = "#FFC1CD", TL = "#FFE4EA", TD = "#4B1F4F";
const RO = "#FF5C7A";
const GR = "#6E4B71", GL = "#FAF7F5", GD = "#6E4B71";
const WH = "#ffffff";

function fmtShort(dk: string): string {
  const d = parseLocalDateKey(dk); if (!d) return dk;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function MenopauseScoreDetail({
  user,
  onBack,
}: {
  user: any;
  onBack: () => void;
}) {
  const [expanded, setExpanded] = useState<TrackedSymptom | null>(null);
  const [data, setData] = useState<Awaited<ReturnType<typeof fetchMenoScore>> | null>(null);
  const [loading, setLoading] = useState(true);
  const todayK = localDateKey();
  const hrtStartDate = user?.hrtStartDate ? parseLocalDateKey(user.hrtStartDate) : null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getUser();
        const uid = sess?.user?.id;
        if (!uid) { if (!cancelled) setLoading(false); return; }
        const d = await fetchMenoScore(uid, 30);
        if (!cancelled) { setData(d); setLoading(false); }
      } catch (e) {
        console.warn("[MenopauseScoreDetail] fetch failed", e);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [todayK]);

  if (loading) {
    return <div style={{ padding: 20, color: GR, fontSize: 13 }}>Loading your score…</div>;
  }
  const series = data?.series ?? [];
  // Headline = TODAY's raw daily score (same as the app card). The rolling
  // 7-day average is shown below as a "Recent average" stat — never as the
  // big headline number. Incomplete-day guard mirrors the card.
  const today = series[series.length - 1];
  const isIncomplete = !today || today.score == null || today.loggedCount < MIN_LOGGED_FOR_INCLUSION;
  const todayScore = isIncomplete ? null : (today!.score as number);
  const band = impactBandFor(todayScore);
  const flagged = data?.flaggedSymptoms ?? [];
  const insight = generateDailyInsight(series, hrtStartDate, flagged);
  // Recent average — 7-day rolling avg of eligible days (≥3 of 9 symptoms
  // logged), same definition as the daily incomplete-day guard.
  const smoothed = computeSmoothedHeadline(series);
  const drivers = rankDriversByImpact(
    data?.severityByDateBySymptom ?? {},
    data?.todayLogsBySymptom ?? {},
    flagged,
  );
  // Flag phrase lives ONLY inside insight.text (single source from
  // generateDailyInsight). On the detail page we append a "(see below)"
  // pointer to the per-symptom drilldown — gated on insight.hasFlag so it
  // can never desync from the flag itself.


  // Build 14-day rows with a trailing 7-day smoothed value computed at each
  // point (eligible days only — same MIN_LOGGED_FOR_INCLUSION rule). Drawn
  // faintly behind the raw line so the eye can see the underlying trend.
  const last14Raw = series.slice(-14);
  const last14 = last14Raw.map((s, i) => {
    // Window = last 7 days ending at this point, eligible-only.
    const startIdx = Math.max(0, series.indexOf(s) - 6);
    const win = series.slice(startIdx, series.indexOf(s) + 1)
      .filter(d => d.score != null && d.loggedCount >= MIN_LOGGED_FOR_INCLUSION) as { score: number }[];
    const smoothedAt = win.length >= 3
      ? Math.round(win.reduce((a, b) => a + b.score, 0) / win.length)
      : null;
    return { dateKey: s.dateKey, label: fmtShort(s.dateKey), score: s.score, smoothed: smoothedAt };
  });
  const hrtIdx = (() => {
    if (!hrtStartDate) return -1;
    const hrtKey = localDateKey(hrtStartDate);
    return last14.findIndex(d => d.dateKey === hrtKey);
  })();

  const gR = 80, gCx = 100, gCy = 100;
  const gAngle = todayScore == null ? 0 : (todayScore / 100) * Math.PI;
  const gx = gCx - gR * Math.cos(gAngle);
  const gy = gCy - gR * Math.sin(gAngle);
  const arcPath = todayScore == null
    ? ""
    : `M ${gCx - gR} ${gCy} A ${gR} ${gR} 0 ${gAngle > Math.PI ? 1 : 0} 1 ${gx.toFixed(1)} ${gy.toFixed(1)}`;

  return (
    <div style={{ paddingBottom: 24 }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: GR, fontSize: 13, cursor: "pointer", marginBottom: 10, padding: "4px 0" }}>
        ← Back
      </button>
      <h1 style={{ fontSize: 22, fontWeight: 600, color: "#FF5C7A", margin: "0 0 4px", letterSpacing: "-0.01em" }}>{getScoreLabel(user?.stage)}</h1>
      <div style={{ fontSize: 12, color: GR, marginBottom: 16 }}>How your symptoms are tracking, day by day.</div>

      <ScoreV2ChangeNote />

      <div style={{ background: WH, border: "0.5px solid #E8D5DA", borderRadius: 18, padding: 18, marginBottom: 14, textAlign: "center" }}>
        <svg width={200} height={120} viewBox="0 0 200 120">
          <path d={`M ${gCx - gR} ${gCy} A ${gR} ${gR} 0 1 1 ${gCx + gR} ${gCy}`} fill="none" stroke={GL} strokeWidth={10} strokeLinecap="round" />
          {arcPath && <path d={arcPath} fill="none" stroke={band.color} strokeWidth={10} strokeLinecap="round" />}
          <text x={gCx} y={gCy - 6} textAnchor="middle" fontSize="44" fontWeight="600" fill={GD}>{todayScore ?? "—"}</text>
          <text x={gCx} y={gCy + 14} textAnchor="middle" fontSize="11" fill={GR}>of 100 · today</text>
        </svg>
        <div>
          <span style={{ display: "inline-block", padding: "5px 14px", borderRadius: 20, background: band.color + "22", color: band.color, fontSize: 12, fontWeight: 500 }}>Today's symptom load: {band.label}{todayScore != null ? ` (${todayScore})` : ""}</span>
        </div>
        <div style={{ fontSize: 11, color: GR, marginTop: 8 }}>
          Based on {today?.loggedCount ?? 0} symptom{(today?.loggedCount ?? 0) === 1 ? "" : "s"} logged today
        </div>
        <div style={{ fontSize: 13, color: GD, lineHeight: 1.5, marginTop: 12, padding: "10px 12px", background: "#FAF7F5", borderRadius: 10 }}>{insight.text}{insight.hasFlag ? " (see below)" : ""}</div>

        {smoothed.score != null && (
          <div style={{ marginTop: 10, fontSize: 12, color: GD, padding: "8px 10px", background: "#FFFFFF", border: "0.5px solid #E8D5DA", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span style={{ color: GR }}>Recent symptom load (last {smoothed.windowDays} days)</span>
            <span style={{ fontWeight: 600, color: GD }}>
              <span style={{ padding: "2px 8px", borderRadius: 12, background: impactBandFor(smoothed.score).color + "22", color: impactBandFor(smoothed.score).color, fontSize: 11, fontWeight: 500 }}>{impactBandFor(smoothed.score).label} ({smoothed.score})</span>
            </span>
          </div>
        )}
      </div>

      <div style={{ background: WH, border: "0.5px solid #E8D5DA", borderRadius: 18, padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: GD, marginBottom: 4 }}>Last 14 days</div>
        <div style={{ fontSize: 11, color: GR, marginBottom: 10 }}>Today is highlighted. Missing days = no log.</div>
        <div style={{ width: "100%", height: 180 }}>
          <ResponsiveContainer>
            <LineChart data={last14} margin={{ top: 20, right: 14, left: -16, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: GR }} interval={1} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: GR }} ticks={[0, 50, 100]} />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: "0.5px solid #E8D5DA" }}
                content={(props: any) => {
                  const { active, payload, label } = props;
                  if (!active || !payload || !payload.length) return null;
                  // Dedupe: dashed underlay + solid line share dataKey "score".
                  // Pick the first non-null value (solid wins on logged days; both
                  // are null on gap days → show "—").
                  const first = payload.find((p: any) => p && p.value != null);
                  const val = first ? first.value : null;
                  return (
                    <div style={{ background: WH, fontSize: 11, borderRadius: 8, border: "0.5px solid #E8D5DA", padding: "6px 10px" }}>
                      <div style={{ color: GR, marginBottom: 2 }}>{label}</div>
                      <div style={{ color: GD }}>Score: {val == null ? "—" : val}</div>
                    </div>
                  );
                }}
              />
              {/* Dashed connector underlay — bridges gaps so the eye follows the
                  trend across missing days. Solid line below paints on top. */}
              <Line type="monotone" dataKey="score" stroke={GD} strokeOpacity={0.5} strokeWidth={1.5}
                    strokeDasharray="3 4" dot={false} activeDot={false} isAnimationActive={false}
                    connectNulls={true} legendType="none" />
              <Line type="monotone" dataKey="score" stroke={band.color} strokeWidth={2} dot={(props: any) => {
                const { cx, cy, index, payload } = props;
                if (payload?.score == null) return <g />;
                const isToday = index === last14.length - 1;
                return <circle key={index} cx={cx} cy={cy} r={isToday ? 5 : 3} fill={isToday ? band.color : WH} stroke={band.color} strokeWidth={isToday ? 2 : 1.5} />;
              }} connectNulls={false}>
                <LabelList dataKey="score" position="top" offset={8}
                  content={(props: any) => {
                    const { x, y, value, index } = props;
                    if (value == null) return null;
                    const n = last14.length;
                    const anchor = index === 0 ? "start" : index === n - 1 ? "end" : "middle";
                    return <text x={x} y={y - 8} fontSize={9} fontWeight={500} fill={GD} textAnchor={anchor}>{Math.round(value)}</text>;
                  }}
                />
              </Line>
              {/* Trailing 7-day smoothed overlay — faint, no dots, no labels.
                  Shows the underlying trend without competing with the raw line. */}
              <Line type="monotone" dataKey="smoothed" stroke={GD} strokeOpacity={0.35}
                    strokeWidth={1.5} dot={false} activeDot={false}
                    isAnimationActive={false} connectNulls={true} legendType="none" />
              {hrtIdx >= 0 && (
                <ReferenceLine x={last14[hrtIdx].label} stroke={TD} strokeDasharray="3 3" label={{ value: "HRT", fontSize: 9, fill: TD, position: "top" }} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ background: WH, border: "0.5px solid #E8D5DA", borderRadius: 18, padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: GD, marginBottom: 4 }}>What's driving your score</div>
        <div style={{ fontSize: 11, color: GR, marginBottom: 12 }}>Biggest impact first. Tap a symptom for its 14-day trend.</div>
        {drivers.length === 0 ? (
          <div style={{ fontSize: 12, color: GR, padding: "12px 0", textAlign: "center" }}>No symptoms logged in the last 30 days.</div>
        ) : drivers.map(d => {
          const isOpen = expanded === d.name;
          const sevMap = data?.severityByDateBySymptom?.[d.name] || {};
          const dates14 = lastNDates(14);
          const sevValues = dates14.map(dk => sevMap[dk] ?? null);
          return (
            <div key={d.name} style={{ borderTop: "0.5px solid #FAF7F5" }}>
              <button onClick={() => setExpanded(isOpen ? null : d.name)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", padding: "10px 0", cursor: "pointer", gap: 8 }}>
                <span style={{ fontSize: 13, color: GD, textAlign: "left", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
                  {d.name}
                  {d.isFlagged && (
                    <span style={{ fontSize: 9, fontWeight: 600, color: "#C24E1A", background: "#FCE4D6", padding: "1px 6px", borderRadius: 8, letterSpacing: "0.04em", textTransform: "uppercase" }}>Flagged</span>
                  )}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  <MiniSpark values={sevValues} />
                  <SeverityDots value={d.severity} />
                  <span style={{ fontSize: 11, color: GR, width: 12, textAlign: "center" }}>{isOpen ? "▾" : "▸"}</span>
                </span>
              </button>
              {isOpen && (
                d.name === "Hot flashes"
                  ? <HotFlashDetail hfCountByDate={data?.hfCountByDate || {}} />
                  : <Severity14d dates={dates14} values={sevValues} />
              )}
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 11, color: GR, lineHeight: 1.55, padding: "0 4px" }}>
        <strong style={{ fontWeight: 500, color: GD }}>How it's calculated:</strong> {METHODOLOGY_DISCLOSURE}
      </div>
    </div>
  );
}

function SeverityDots({ value }: { value: number }) {
  return (
    <span style={{ display: "inline-flex", gap: 3 }}>
      {[1, 2, 3, 4].map(i => (
        <span key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: i <= value ? RO : GL }} />
      ))}
    </span>
  );
}

function MiniSpark({ values }: { values: (number | null)[] }) {
  const hasAny = values.some(v => v != null);
  if (!hasAny) return <span style={{ display: "inline-block", width: 56, height: 20 }} />;
  const points = values.map((v, i) => ({ i, severity: v }));
  const nonNullCount = values.reduce((a: number, v) => a + (v != null ? 1 : 0), 0);
  return (
    <span style={{ display: "inline-block", width: 56, height: 20 }}>
      <ResponsiveContainer>
        <LineChart data={points} margin={{ top: 2, right: 1, left: 1, bottom: 2 }}>
          <YAxis hide domain={[0, 4]} />
          <XAxis hide dataKey="i" />
          <Line type="monotone" dataKey="severity" stroke={GD} strokeOpacity={0.7}
                strokeWidth={1.5}
                dot={nonNullCount === 1 ? { r: 1.5, fill: GD } : false}
                isAnimationActive={false} connectNulls={true} />
        </LineChart>
      </ResponsiveContainer>
    </span>
  );
}

function Severity14d({ dates, values }: { dates: string[]; values: (number | null)[] }) {
  const rows = dates.map((dk, i) => ({ label: fmtShort(dk), severity: values[i] ?? null }));
  return (
    <div style={{ width: "100%", height: 100, marginBottom: 10 }}>

      <ResponsiveContainer>
        <LineChart data={rows} margin={{ top: 16, right: 12, left: -24, bottom: 0 }}>
          <XAxis dataKey="label" tick={{ fontSize: 8, fill: GR }} interval={2} />
          <YAxis domain={[0, 4]} ticks={[1, 2, 3, 4]} tick={{ fontSize: 8, fill: GR }} />
          <Tooltip
            contentStyle={{ fontSize: 10, borderRadius: 6 }}
            content={(props: any) => {
              const { active, payload, label } = props;
              if (!active || !payload || !payload.length) return null;
              // Dedupe: dashed underlay + solid line share dataKey "severity".
              const first = payload.find((p: any) => p && p.value != null);
              const val = first ? first.value : null;
              return (
                <div style={{ background: WH, fontSize: 10, borderRadius: 6, border: "0.5px solid #E8D5DA", padding: "5px 8px" }}>
                  <div style={{ color: GR, marginBottom: 2 }}>{label}</div>
                  <div style={{ color: GD }}>Severity: {val == null ? "—" : val}</div>
                </div>
              );
            }}
          />
          {/* Dashed connector underlay — bridges gaps so the eye follows the
              trend across missing days. Solid line below paints on top. */}
          <Line type="monotone" dataKey="severity" stroke={GD} strokeOpacity={0.5} strokeWidth={1.5}
                strokeDasharray="3 4" dot={false} activeDot={false} isAnimationActive={false}
                connectNulls={true} legendType="none" />
          <Line type="monotone" dataKey="severity" stroke={RO} strokeWidth={1.8}
                dot={{ r: 2, fill: RO }} connectNulls={false}>
            <LabelList dataKey="severity" position="top" offset={8}
              content={(props: any) => {
                const { x, y, value, index } = props;
                if (value == null) return null;
                const n = rows.length;
                const anchor = index === 0 ? "start" : index === n - 1 ? "end" : "middle";
                return <text x={x} y={Number(y) - 8} fontSize={9} fontWeight={500} fill={GD} textAnchor={anchor}>{Math.round(Number(value))}</text>;
              }}
            />
          </Line>
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function HotFlashDetail({ hfCountByDate }: { hfCountByDate: Record<string, number> }) {
  const dates = useMemo(() => lastNDates(14), []);
  const bars = dates.map(dk => ({ label: fmtShort(dk), count: hfCountByDate[dk] || 0 }));
  const total14 = bars.reduce((a, b) => a + b.count, 0);
  const baseColor = RO;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: GR, marginBottom: 4 }}>{total14} events in last 14 days</div>
      <div style={{ width: "100%", height: 110 }}>
        <ResponsiveContainer>
          <BarChart data={bars} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 8, fill: GR }} interval={2} />
            <YAxis tick={{ fontSize: 8, fill: GR }} allowDecimals={false} />
            <Tooltip contentStyle={{ fontSize: 10, borderRadius: 6 }} />
            <Bar dataKey="count" radius={[2, 2, 0, 0]}>
              {bars.map((b, i) => (
                <Cell key={i} fill={heatCellColor({ symptomKey: "Hot flashes", severity: null, eventCount: b.count, baseColor })} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: "flex", gap: 1, marginTop: 4 }}>
        {bars.map((b, i) => (
          <div key={i} style={{ flex: 1, height: 14, borderRadius: 2, background: heatCellColor({ symptomKey: "Hot flashes", severity: null, eventCount: b.count, baseColor }) }} title={`${b.label}: ${b.count}`} />
        ))}
      </div>
    </div>
  );
}
